import React, { useMemo } from 'react'

import type { Tier } from '@api'
import { getCurrency } from '@common/utils/currency.ts'
import { type ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import EditIcon from '@/assets/icons/icon-edit.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $tiers } from '@/models/lists/tiers.ts'

interface Props {}

export const TiersTable: React.FC<Props> = () => {
  const tiers = useUnit($tiers)
  const columns = useMemo<ColumnDef<Tier>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) =>
          row.original.onlyFansId ? (
            <CreatorBrowserLink
              href={`https://onlyfans.com/my/collections/user-lists/${row.original.onlyFansId}`}
              tooltip="Open in OnlyFans"
            >
              <a
                className="text-primary-500"
                rel="noopener noreferrer"
                target="_blank"
              >
                {row.original.name}
              </a>
            </CreatorBrowserLink>
          ) : (
            <Tooltip
              content="No list found"
              className="text-left"
            >
              {row.original.name}
            </Tooltip>
          ),
      },
      {
        accessorKey: 'min',
        header: 'Min Spend',
        cell: ({ row }) => getCurrency(row.original.minSpend),
      },
      {
        accessorKey: 'max',
        header: 'Max Spend',
        cell: ({ row }) => getCurrency(row.original.maxSpend),
      },
      {
        accessorKey: 'includeExpired',
        header: 'Incl. Expired',
        cell: ({ row }) =>
          row.original.includeExpiredFans ? (
            <CheckIcon className="text-title" />
          ) : (
            <CloseIcon className="text-title" />
          ),
      },
      {
        id: 'actions',
        header: () => <span className="ml-auto">Actions</span>,
        cell: ({ row }) => (
          <div className="float-right flex gap-4">
            <EditIcon
              width={18}
              height={18}
              className="cursor-pointer text-primary-500"
              onClick={() =>
                openDialog({
                  type: DIALOG_TYPE.TIERS_DIALOG,
                  tierId: row.original.id!,
                  testId: 'edit-list-dialog',
                })
              }
              data-testid="edit-icon"
            >
              Edit
            </EditIcon>
            <RemoveIcon
              width={18}
              height={18}
              className="text-danger-default cursor-pointer"
              onClick={() =>
                openDialog({
                  type: DIALOG_TYPE.REMOVE_TIER_DIALOG,
                  tierId: row.original.id!,
                })
              }
              data-testid="remove-icon"
            >
              Remove
            </RemoveIcon>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <DataTable
      testId="fan-spend-lists-table"
      columns={columns}
      data={tiers}
    />
  )
}
